package puzzlekey

import kotlinx.serialization.Serializable
import kotlinx.serialization.SerializationException
import kotlinx.serialization.json.Json
import kotlin.io.encoding.Base64
import kotlin.random.Random

/**
 * Envelope encryption (ADR-0006): the Bundle is encrypted under a random Data key, and the Data key is
 * wrapped under `HKDF(QR secret ‖ x)` for each route. On the Puzzle route x is
 * `PBKDF2-SHA256(Puzzle bits)`, the slow KDF (ADR-0009 §5, ADR-0012); the Recovery code needs none.
 */
object Envelope {
    /**
     * Seals [bundle]. [puzzleBits] are the Enrollment's reference, in [scan]'s mask order; they never leave
     * this call. [iterations] is [PBKDF2_ITERATIONS] for a real seal, fewer only in tests. [radar] is
     * public, like [scan]: the Radar's thresholds (ADR-0013 §3).
     */
    suspend fun seal(
        bundle: ByteArray,
        qrSecret: ByteArray,
        puzzleBits: BooleanArray,
        recoveryCode: RecoveryCode,
        scan: ScanParams,
        iterations: Int,
        random: Random,
        radar: RadarParams = RadarParams(),
    ): SealedBundle {
        requireFits(puzzleBits, scan)
        val salt = random.nextBytes(16)
        val pbkdf2 = Pbkdf2Params(salt = random.nextBytes(16).b64(), iterations = iterations)
        val dataKey = random.nextBytes(32)
        val iv = random.nextBytes(12)
        return SealedBundle(
            v = VERSION,
            salt = salt.b64(),
            iv = iv.b64(),
            ciphertext = Crypto.encrypt(dataKey, iv, bundle).b64(),
            wrap = Wraps(
                puzzle = wrap(dataKey, wrappingKey(qrSecret, slowKdf(puzzleBits, pbkdf2), salt, PUZZLE), random),
                recovery = wrap(dataKey, wrappingKey(qrSecret, recoveryCode.bytes(), salt, RECOVERY), random),
            ),
            pbkdf2 = pbkdf2,
            scan = scan,
            radar = radar,
        )
    }

    /** One candidate's try (ADR-0009 §4): about [PBKDF2_ITERATIONS] worth of time, so the site tries few per frame. */
    suspend fun openWithPuzzle(sealed: SealedBundle, qrSecret: ByteArray, puzzleBits: BooleanArray): OpenResult {
        requireFits(puzzleBits, sealed.scan)
        return open(sealed, sealed.wrap.puzzle, qrSecret, slowKdf(puzzleBits, sealed.pbkdf2), PUZZLE)
    }

    /**
     * One Scan's try (ADR-0009 §4): the [candidates] of its [projection] under `sealed.json`'s Error
     * tolerance, back to back, until one opens the wrap. A [OpenResult.Damaged] stops it too: the key was right.
     */
    suspend fun openWithScan(sealed: SealedBundle, qrSecret: ByteArray, projection: DoubleArray): ScanAttempt {
        val guesses = candidates(projection, sealed.scan.mask, sealed.scan.maxErrors, sealed.scan.candidatesPerFrame)
        for ((i, bits) in guesses.withIndex()) {
            val result = openWithPuzzle(sealed, qrSecret, bits)
            if (result != OpenResult.WrongKey) return ScanAttempt(result, tried = i + 1)
        }
        return ScanAttempt(OpenResult.WrongKey, tried = guesses.size)
    }

    suspend fun openWithRecovery(sealed: SealedBundle, qrSecret: ByteArray, recoveryCode: RecoveryCode): OpenResult =
        open(sealed, sealed.wrap.recovery, qrSecret, recoveryCode.bytes(), RECOVERY)

    /** The `sealed.json` format version this code reads and writes. */
    const val VERSION = 2

    private const val PUZZLE = "puzzle"
    private const val RECOVERY = "recovery"

    private suspend fun open(sealed: SealedBundle, wrapped: WrappedKey, qrSecret: ByteArray, x: ByteArray, route: String): OpenResult {
        val dataKey = try {
            unwrap(wrapped, wrappingKey(qrSecret, x, sealed.salt.unb64(), route))
        } catch (e: AeadException) {
            return OpenResult.WrongKey
        }
        return try {
            OpenResult.Opened(Crypto.decrypt(dataKey, sealed.iv.unb64(), sealed.ciphertext.unb64()))
        } catch (e: AeadException) {
            OpenResult.Damaged
        }
    }

    private fun requireFits(puzzleBits: BooleanArray, scan: ScanParams) =
        require(puzzleBits.size == scan.mask.size) { "${puzzleBits.size} Puzzle bits for a ${scan.mask.size}-bit mask" }

    private suspend fun slowKdf(puzzleBits: BooleanArray, params: Pbkdf2Params) =
        Crypto.pbkdf2(packBits(puzzleBits), params.salt.unb64(), params.iterations, 32)

    /** The canonical symbols, so any typed form of the same code derives the same key. */
    private fun RecoveryCode.bytes() = symbols.encodeToByteArray()

    private suspend fun wrappingKey(qrSecret: ByteArray, x: ByteArray, salt: ByteArray, route: String) =
        Crypto.hkdf(ikm = qrSecret + x, salt = salt, info = route.encodeToByteArray(), length = 32)

    private suspend fun wrap(dataKey: ByteArray, wrappingKey: ByteArray, random: Random): WrappedKey {
        val iv = random.nextBytes(12)
        return WrappedKey(iv = iv.b64(), key = Crypto.encrypt(wrappingKey, iv, dataKey).b64())
    }

    private suspend fun unwrap(wrapped: WrappedKey, wrappingKey: ByteArray): ByteArray =
        Crypto.decrypt(wrappingKey, wrapped.iv.unb64(), wrapped.key.unb64())
}

sealed interface OpenResult {
    class Opened(val bundle: ByteArray) : OpenResult

    /** The wrap didn't open: wrong QR secret, Puzzle bits or Recovery code, or a tampered wrap. */
    data object WrongKey : OpenResult

    /** The Data key unwrapped, but the Bundle ciphertext doesn't authenticate. */
    data object Damaged : OpenResult
}

/** What [Envelope.openWithScan] came to, after trying [tried] candidates. */
class ScanAttempt(val result: OpenResult, val tried: Int)

/**
 * `sealed.json` v2: what `seal` writes and the site loads. Byte fields are base64. Everything in it is
 * public (ADR-0001): the ciphertext, the wraps, the helper data a Scan needs ([scan], [pbkdf2]) and the
 * Radar's thresholds ([radar]).
 */
@Serializable
class SealedBundle(
    val v: Int,
    val salt: String,
    val iv: String,
    val ciphertext: String,
    val wrap: Wraps,
    val pbkdf2: Pbkdf2Params,
    val scan: ScanParams,
    /** Optional in the file: a `sealed.json` from before T14 gets ADR-0013's defaults. */
    val radar: RadarParams = RadarParams(),
) {
    fun toJson(): String = Json.encodeToString(serializer(), this)

    companion object {
        /** Null when the JSON is broken, from a format version this code doesn't know, or unusable by a Scan. */
        fun fromJson(json: String): SealedBundle? =
            try {
                Json.decodeFromString(serializer(), json).takeIf { it.v == Envelope.VERSION && it.byteFieldsDecode() && it.paramsUsable() }
            } catch (e: SerializationException) {
                null
            }
    }
}

private fun SealedBundle.byteFieldsDecode(): Boolean =
    listOf(salt, iv, ciphertext, wrap.puzzle.iv, wrap.puzzle.key, wrap.recovery.iv, wrap.recovery.key, pbkdf2.salt)
        .all { runCatching { it.unb64() }.isSuccess }

private fun SealedBundle.paramsUsable(): Boolean =
    pbkdf2.iterations > 0 &&
        scan.maxErrors >= 0 && scan.candidatesPerFrame >= 1 &&
        scan.mask.isNotEmpty() && scan.mask.all { it in 0 until PROJECTIONS } &&
        scan.mask.toList().zipWithNext().all { (a, b) -> a < b } &&
        radar.cold < radar.hot

/**
 * What a Scan needs to produce the Puzzle bits (ADR-0009 §1, §2, §4): the model it was enrolled with,
 * the projection [seed] and [centre], the [mask] (ascending projection indices), and the Error tolerance.
 * Public helper data: none of it gives the bits away (the mask passed ADR-0011's leak gate).
 */
@Serializable
class ScanParams(
    val model: String,
    val seed: Int,
    val centre: FloatArray,
    val mask: IntArray,
    val maxErrors: Int,
    val candidatesPerFrame: Int,
)

/** The Puzzle route's slow KDF. Changing either value means a re-seal. */
@Serializable
class Pbkdf2Params(val salt: String, val iterations: Int)

@Serializable
class Wraps(val puzzle: WrappedKey, val recovery: WrappedKey)

@Serializable
class WrappedKey(val iv: String, val key: String)

private fun ByteArray.b64() = Base64.encode(this)

private fun String.unb64() = Base64.decode(this)
